/**
 * Copyright IBM Corp. 2022, 2026
 */

import { render as toHtml } from 'datocms-structured-text-to-html-string'
import { render as toPlainText } from 'datocms-structured-text-to-plain-text'
import { Badge, Text, Card } from '@hashicorp/mds-react/components'
import type {
	AllConferenceAgendaItemsQuery,
	AllConferenceSessionsQuery,
} from 'types/dato'
import {
	getConferenceBadgeProps,
	getDateSlugFromStartTime,
	getTime,
	productTypeOverrides,
	type IBadge,
} from 'app/[locale]/conferences/lib/utils'
import { vercelSplit } from '@web/cms'
import AddToCalendarDropdown from 'components/materia/add-to-calendar-dropdown'
import s from './agenda-card.module.css'

interface IAgendaCard {
	session: TAgendaCard
}

type AgendaItem =
	AllConferenceAgendaItemsQuery['allConferenceGeneralSessions'][number]
type SessionItem = AllConferenceSessionsQuery['allConferenceSessions'][number]
type TAgendaCard = AgendaItem | SessionItem

const AgendaCard = ({ session }: IAgendaCard) => {
	const { conference, title, location, startTime, endTime } = session

	let slug
	let href
	let badges: IBadge[]
	let description: undefined | SessionItem['description']

	const conferenceTrimmed = vercelSplit(conference)
	const startTimeTrimmed = vercelSplit(startTime)
	const start = getTime(new Date(startTimeTrimmed))

	const isInTheFuture = new Date(startTimeTrimmed) > new Date()

	const end = getTime(new Date(vercelSplit(endTime)))
	const dateSlug = getDateSlugFromStartTime(vercelSplit(startTime))
	let hasVideo

	if (session.__typename === 'ConferenceSessionRecord') {
		slug = session.slug
		badges = session?.badges ? session?.badges : null
		href = `/conferences/${conferenceTrimmed}/agenda/${dateSlug}/${slug}`
		hasVideo = !!session?.videoUrl
		description = session?.description
	}

	const ctaLabel = hasVideo ? 'Watch now' : 'Learn more'

	const productBadges =
		badges?.length > 0
			? badges
					.filter((badge) => {
						const trimmedBadgeText = vercelSplit(badge.text)
						const isProductType = productTypeOverrides[trimmedBadgeText]
						return isProductType
					})
					.map((badge) => {
						const trimmedBadgeText = vercelSplit(badge.text)
						return {
							...getConferenceBadgeProps(badge),
							text: productTypeOverrides[trimmedBadgeText],
						}
					})
			: null
	return (
		<Card className={s.root} href={href} ctaLabel={ctaLabel}>
			<Text.DisplayExpressive size="200" color="strong" className={s.text}>
				{title}
			</Text.DisplayExpressive>
			<div>
				<Text.Body tag="p" color="primary" size="200" className={s.text}>
					{`${start} - ${end}`}
				</Text.Body>
				{location ? (
					<Text.Body tag="p" color="primary" size="200" className={s.text}>
						Location: {location}
					</Text.Body>
				) : null}
				{productBadges?.length ? (
					<ul className={s.badges}>
						{productBadges.map((badge) => {
							return (
								<li key={badge.text}>
									<Badge
										icon={badge.icon}
										iconColor={badge.color}
										text={badge.text}
									/>
								</li>
							)
						})}
					</ul>
				) : null}
			</div>
			{isInTheFuture ? (
				<AddToCalendarDropdown
					dropdownCta="Add to calendar"
					event={{
						name: title,
						details: description
							? {
									plainText: vercelSplit(toPlainText(description)),
									html: vercelSplit(toHtml(description)),
								}
							: null,
						location: location,
						startsAt: vercelSplit(startTime),
						endsAt: vercelSplit(endTime),
					}}
				/>
			) : null}
		</Card>
	)
}

export default AgendaCard
